import React, { useState } from 'react';
import { trackEvent } from '../utils/tracking';

/**
 * WhatsApp-Floating-Button
 * Fest unten rechts auf jeder öffentlichen Seite (Desktop + Mobile).
 *
 * DSGVO-konform: Es wird KEINE Verbindung zu WhatsApp aufgebaut,
 * solange der Nutzer nicht klickt. Reiner <a href>-Link, kein Embed,
 * kein iFrame, kein externes JavaScript.
 *
 * Verhalten:
 *  - Mobile: öffnet WhatsApp-App direkt (wa.me öffnet App-Intent)
 *  - Desktop: öffnet WhatsApp Web (wa.me öffnet web.whatsapp.com)
 *  - Kein vorausgefüllter Text — der Kunde formuliert frei.
 */
const WHATSAPP_NUMBER_E164 = '4942212959978'; // Green Lion Energy Festnetz mit Ländercode
const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER_E164}`;

export default function WhatsAppFloatingButton() {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <div
      className="hidden md:flex fixed bottom-5 right-4 md:bottom-6 md:right-6 z-[60] flex-col items-end gap-2"
      data-testid="whatsapp-floating-container"
    >
      {/* Mobile-Hinweis (nur < 768px sichtbar) — reine CSS-Lösung */}
      <span
        className="md:hidden bg-white/95 backdrop-blur-sm text-slate-700 text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-md border border-slate-200/80 select-none"
        data-testid="whatsapp-mobile-hint"
      >
        Kostenlose Beratung per WhatsApp
      </span>

      {/* Desktop-Tooltip (nur ≥ 768px, nur bei Hover/Focus) */}
      {tooltipOpen && (
        <span
          className="hidden md:inline-block bg-slate-900 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap mr-1 mb-1"
          data-testid="whatsapp-tooltip"
          role="tooltip"
        >
          💬 Fragen? Schreiben Sie uns einfach per WhatsApp.
        </span>
      )}

      {/* Der eigentliche Button — reiner <a>, kein externer Script-Load */}
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp – kostenlose Beratung anfragen"
        title="Fragen? Schreiben Sie uns einfach per WhatsApp."
        onClick={() => {
          // Conversion-Event NOCH VOR Navigation feuern — sendBeacon im
          // Helper sorgt dafür, dass es zuverlässig ankommt.
          // Blockiert den Klick nicht, kein await.
          trackEvent('whatsapp_click', { source: 'floating_whatsapp_button' });
        }}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
        className="
          group relative flex items-center justify-center
          h-14 w-14 md:h-[60px] md:w-[60px]
          rounded-full
          bg-[#25D366] hover:bg-[#1ebe57]
          shadow-[0_8px_24px_rgba(37,211,102,0.45)]
          hover:shadow-[0_10px_30px_rgba(37,211,102,0.6)]
          transition-all duration-200 ease-out
          hover:scale-[1.06] active:scale-95
          focus:outline-none focus:ring-4 focus:ring-emerald-300/60
        "
        data-testid="whatsapp-floating-button"
      >
        {/* Sanfter Puls als Aufmerksamkeits-Akzent */}
        <span
          className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping"
          style={{ animationDuration: '2.4s' }}
          aria-hidden="true"
        />
        {/* Offizielles WhatsApp-Logo — Inline-SVG, kein externer Asset */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className="relative z-10 h-7 w-7 md:h-8 md:w-8 text-white"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.521.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
        </svg>
      </a>
    </div>
  );
}
